import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_BP,
  RECEIVE_ALL_BP,
  RECEIVE_BP_METADATA,
  INCREMENT_BP_COUNT,
  RECEIVE_BP_LINES,
  RECEIVE_ALL_BP_LINES,
  RECEIVING_BP_LINES,
  RECEIVE_BP_LINES_METADATA,
  RECEIVE_BP_LINES_FAILURE,
  SAVING_BP_LINES,
  UPDATING_BP_LINES,
  CLEAR_BP_STATE,
  CLEAR_STATE,
  RESET_BP_META,
  UPDATE_CURRENT_BP_PAGE,
  DELETE_BP_LINES,
  CLEAR_BP_TRX_LINES
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { fetchLines, getLineIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  clearState,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const lineSchema = new schema.Entity('lines')
const bpSchema = new schema.Entity('bps', { lines: new schema.Array(lineSchema) })
const bpListSchema = new schema.Array(bpSchema)

const state = {
  cache: {},
  all: [],
  allLines: [],
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null
}

const actions = {
  /**
   * Fetches one or more BPs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchBpsWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = null
    }
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await http.get(buildUrlWithQuery('bps', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_BP_META)
      let { results, next, previous, count } = resp
      const { result, entities } = normalize(results, bpListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.bps }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      this.commit(RECEIVE_BP, mutationPayload)
      this.commit(RECEIVE_BP_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_BP_PAGE, 1)
      if (storeLines) {
        const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.bps), type: 'bp' })
        this.commit(RECEIVE_BP_LINES, { id: null, data: lines })
      }
      return { query, count, entities }
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Fetches all available BPs
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllBps (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await http.get(buildUrl('bps?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVE_ALL_BP, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Fetches all available BP lines
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllBpLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await http.get(buildUrl('lines/bp?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVE_ALL_BP_LINES, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_BP_LINES_FAILURE, e.statusText)
    }
  },
  /**
   * Saves a BP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async saveBp (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await http.post(buildUrl('bps', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save BP`)
      }
      const { entities, result } = normalize(resp, bpSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving BP`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.bps), type: 'bp' })
      this.commit(RECEIVE_BP, { data: entities.bps })
      this.commit(RECEIVE_BP_LINES, { id: null, data: lines })
      this.commit(INCREMENT_BP_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates a BP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updateBp (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await http.patch(buildUrl(`bps/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update BP with id ${id}`)
      }
      const { entities, result } = normalize(resp, bpSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating BP with id ${id}`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.bps), type: 'bp' })
      this.commit(RECEIVE_BP, { data: entities.bps })
      this.commit(RECEIVE_BP_LINES, { id: null, data: lines })
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Deletes an BP transaction
   * @param store
   * @param id
   * @return {Promise<*>}
   */
  async deleteBp (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      return await http.delete(buildUrlWithQuery(`bps/${id}`, null, instanceUrl), { headers })
        .then(checkStatus)
    } catch (e) {
      console.log(e)
      return e
    }
  },
  clearBpState (store, payload = null) {
    return this.commit(CLEAR_BP_STATE, payload)
  },
  clearBpTransactionLines (store, apId) {
    this.commit(CLEAR_BP_TRX_LINES, apId)
  },
  resetBpMeta () {
    this.commit(RESET_BP_META)
  }
}

const getters = {
  getBps: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getBpById: (state) => (id) => {
    if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllBps: (state) => {
    return state.all || []
  },
  getBpMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getBpLines: (state) => {
    return state.lines || {}
  },
  getAllBpLines: (state) => {
    return state.allLines || []
  },
  getBpLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getBpLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextBpLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevBpLineSet: (state) => {
    return state.prevLineUrl
  },
  getBpsByNumber: (state) => (number) => {
    return Object.values(state.cache).filter(v => v.number.includes(number))
  },
  getCurrentBpPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [RECEIVE_BP] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Bill Payment'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_BP] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_BP_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_BP_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_ALL_BP_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [SAVING_BP_LINES] (state, payload) {
    state.savingLines = { status: payload.status, err: null }
  },
  [UPDATING_BP_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [INCREMENT_BP_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_BP_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_BP_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_BP_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_BP_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      deleting: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        for (let i = 0; i < payload.length; i++) {
          if (newState.hasOwnProperty(payload[i])) delete newState[payload[i]]
        }
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_BP_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_BP_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [DELETE_BP_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
